import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { AppHeader } from "./AppHeader";
import { BottomNav } from "./BottomNav";
import UserSidebar from "./UserSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, WifiOff } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useUserRole } from "@/hooks/useUserRole";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useSubscriptionGate, isGateAllowedPath } from "@/hooks/useSubscriptionGate";

export function AppLayout() {
  const isMobile = useIsMobile();
  const { user, loading: authLoading } = useAuth();
  const { isComplete, isLoading: onboardingLoading } = useOnboardingStatus();
  const { isCompanyOwner, isAdmin, isSystemOwner, isLoading: rolesLoading } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasActiveSub, loading: gateLoading, realtimeStatus } = useSubscriptionGate();
  const warnedRef = useRef(false);
  const canManageBilling = isCompanyOwner || isAdmin || isSystemOwner;
  const gateBlocked = hasActiveSub === false && !isGateAllowedPath(location.pathname, canManageBilling);

  useEffect(() => {
    if (realtimeStatus === "error" && !warnedRef.current) {
      warnedRef.current = true;
      toast.warning("Realtidsuppdatering otillgänglig", {
        description: "Vi synkar mot servern var 30:e sekund istället. Planaktiveringen kan dröja något.",
      });
    }
    if (realtimeStatus === "connected") {
      warnedRef.current = false;
    }
  }, [realtimeStatus]);

  useEffect(() => {
    if (authLoading || onboardingLoading) return;
    if (!user) {
      navigate("/auth", { replace: true });
    } else if (!isComplete) {
      // Admin/system_owner have isComplete=true via useOnboardingStatus, so they
      // never get bounced here. Regular users without a company go to onboarding.
      navigate("/onboarding", { replace: true });
    }
  }, [authLoading, onboardingLoading, user, isComplete, navigate]);

  // Subscription gate: redirect blocked routes to /companies?tab=subscription
  useEffect(() => {
    if (authLoading || onboardingLoading || gateLoading || rolesLoading) return;
    if (!user || !isComplete) return;
    if (hasActiveSub === false && !isGateAllowedPath(location.pathname, canManageBilling)) {
      // Owners/admins can manage billing → send them to subscription tab.
      // Employees can't change billing → show a friendly paused-state page.
      if (canManageBilling) {
        navigate("/companies?tab=subscription", { replace: true });
      } else {
        navigate("/subscription-paused", { replace: true });
      }
    }
  }, [authLoading, onboardingLoading, gateLoading, rolesLoading, user, isComplete, hasActiveSub, canManageBilling, location.pathname, navigate]);

  if (authLoading || (user && onboardingLoading) || !user || !isComplete) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Hide nav chrome when subscription gate is active so user only has profile + logout access
  const hideNav = hasActiveSub === false;

  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col w-full bg-background">
        <AppHeader hideSearch={hideNav} />
        <div className="flex-1 flex">
          {!isMobile && !hideNav && <UserSidebar />}
          <main
            className={`grain relative flex-1 px-4 md:px-6 lg:px-8 py-6 overflow-y-auto ${
              isMobile ? (hideNav ? "" : "pb-24") : (hideNav ? "" : "md:ml-[88px]")
            }`}
          >
            {hideNav && realtimeStatus === "error" && (
              <Alert variant="destructive" className="mb-4">
                <WifiOff className="h-4 w-4" />
                <AlertTitle>Realtidsuppdatering otillgänglig</AlertTitle>
                <AlertDescription>
                  Vi kunde inte ansluta till realtidstjänsten. Spärren kan vara fördröjd — vi synkar automatiskt mot servern var 30:e sekund. Du kan också klicka på "Uppdatera" för att kontrollera direkt.
                </AlertDescription>
              </Alert>
            )}
            {gateBlocked ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
        {isMobile && !hideNav && <BottomNav />}
      </div>
    </TooltipProvider>
  );
}
